import { useTranslation } from 'react-i18next';
import { Link2, Plus } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { TooltipProvider } from '@/shared/components/ui/Tooltip';
import { ActiveConnectionCard } from './ActiveConnectionCard';
import { WebhookButton } from './WebhookButton';
import { DeleteIntegrationButton } from './DeleteIntegrationButton';
import { IntegrationProvider } from '@momentum/types';
import type { GetAllIntegrationsApiResponse } from '@momentum/types';

interface ConnectionsTabProps {
  integrations: GetAllIntegrationsApiResponse;
  companyId: string;
  onSync: (integrationId: string, provider: string) => void;
  isSyncing: boolean;
  canDelete: boolean;
  onBrowse: () => void;
}

export const ConnectionsTab = ({
  integrations,
  companyId,
  onSync,
  isSyncing,
  canDelete,
  onBrowse,
}: ConnectionsTabProps) => {
  const { t } = useTranslation('goals');
  const hasIntegrations = integrations.length > 0;

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="space-y-4">
        {hasIntegrations ? (
          <TooltipProvider>
            {integrations.map((integration) => {
              const isJiraProvider =
                integration.provider === IntegrationProvider.JIRA;

              return (
                <ActiveConnectionCard
                  key={integration._id}
                  provider={integration.provider}
                  onSync={() => onSync(integration._id, integration.provider)}
                  isSyncing={isSyncing}
                  showSync={isJiraProvider}
                  webhookSlot={
                    isJiraProvider ? (
                      <WebhookButton
                        integration={integration}
                        companyId={companyId}
                      />
                    ) : undefined
                  }
                  deleteSlot={
                    canDelete ? (
                      <DeleteIntegrationButton
                        companyId={companyId}
                        integrationId={integration._id}
                        provider={integration.provider}
                      />
                    ) : undefined
                  }
                />
              );
            })}
          </TooltipProvider>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
              <Link2 className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">
              {t('integrationsDialog.noOtherIntegrations')}
            </p>
            <p className="mt-1 text-xs text-muted-foreground/60">
              {t('integrationsDialog.noOtherIntegrationsDescription')}
            </p>
            <Button size="sm" className="mt-4 gap-2" onClick={onBrowse}>
              <Plus className="h-4 w-4" />
              {t('integrationsDialog.browseIntegrations')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
